import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';

/** Public invoice token from quote / accept response (backend may use any of these shapes). */
function extractInvoiceShareToken(data) {
  if (!data || typeof data !== 'object') return null;
  const t =
    data.invoice_share_token ||
    data.invoice_public_token ||
    data.public_invoice_token ||
    data.share_token ||
    data.invoice?.share_token ||
    data.Invoice?.share_token ||
    (data.linked_invoice && (data.linked_invoice.share_token || data.linked_invoice.public_token));
  return t && String(t).trim() ? String(t).trim() : null;
}

/** Parse resolve endpoints: { data: { share_token } }, flat share_token, or nested invoice fields. */
function shareTokenFromResolveResponse(res) {
  const body = res?.data;
  if (!body || typeof body !== 'object') return null;
  const d = body.data;
  return (
    extractInvoiceShareToken(d) ||
    extractInvoiceShareToken(body) ||
    (typeof d?.share_token === 'string' && d.share_token.trim()) ||
    (typeof body.share_token === 'string' && body.share_token.trim()) ||
    null
  );
}

/** When the public quote row is gone, try known backend paths that return the invoice public token. */
async function fetchInvoiceShareTokenByQuoteShare(apiUrl, shareToken) {
  const enc = encodeURIComponent(shareToken);
  const paths = [
    `/invoice/quotes/public/${enc}/invoice-share`,
    `/invoice/quotes/public/${enc}/linked-invoice`,
    `/invoice/from-quote-share/${enc}`,
  ];
  for (const path of paths) {
    try {
      const res = await axios.get(`${apiUrl}${path}`);
      const tok = shareTokenFromResolveResponse(res);
      if (tok) return tok;
    } catch (_) {
      /* try next */
    }
  }
  return null;
}

const STATUS_BADGE = {
  draft:    { label: 'Pending Review', cls: 'bg-yellow-100 text-yellow-800' },
  sent:     { label: 'Awaiting Response', cls: 'bg-blue-100 text-blue-800' },
  accepted: { label: 'Accepted', cls: 'bg-green-100 text-green-800' },
  declined: { label: 'Declined', cls: 'bg-red-100 text-red-800' },
  cancelled:{ label: 'Cancelled', cls: 'bg-gray-100 text-gray-600' },
};

const QuotePublicView = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [companyLegalName, setCompanyLegalName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [responding, setResponding] = useState(false);
  const [responded, setResponded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const tryRedirectToInvoice = (shareTok) => {
      if (!shareTok || cancelled) return false;
      navigate(`/invoice/view/${shareTok}`, { replace: true });
      return true;
    };

    const fetchQuote = async () => {
      try {
        const [quoteRes, brandRes] = await Promise.allSettled([
          axios.get(`${apiUrl}/invoice/quotes/public/${token}`),
          axios.get(`${apiUrl}/settings/branding`),
        ]);
        if (brandRes.status === 'fulfilled' && brandRes.value.data && !cancelled) {
          const b = brandRes.value.data;
          if (b.logo_url) setLogoUrl(b.logo_url);
          if (b.company_legal_name) setCompanyLegalName(b.company_legal_name);
        }

        if (quoteRes.status === 'fulfilled' && quoteRes.value.data.success) {
          const d = quoteRes.value.data.data;
          const invTok = extractInvoiceShareToken(d);
          const shouldRedirect =
            invTok &&
            (d.status === 'accepted' ||
              d.converted_to_invoice === true ||
              d.quote_converted === true);
          if (shouldRedirect && tryRedirectToInvoice(invTok)) return;
          if (!cancelled) setQuote(d);
        } else if (quoteRes.status === 'fulfilled') {
          const body = quoteRes.value.data;
          const invTokFromBody =
            extractInvoiceShareToken(body?.data) ||
            extractInvoiceShareToken(body) ||
            shareTokenFromResolveResponse(quoteRes.value);
          if (invTokFromBody && tryRedirectToInvoice(invTokFromBody)) return;

          const resolved = await fetchInvoiceShareTokenByQuoteShare(apiUrl, token);
          if (resolved && tryRedirectToInvoice(resolved)) return;

          if (!cancelled) {
            setError(body?.message || 'Quote not found or this link has expired.');
          }
        } else {
          const resolved = await fetchInvoiceShareTokenByQuoteShare(apiUrl, token);
          if (resolved && tryRedirectToInvoice(resolved)) return;
          if (!cancelled) {
            setError('Quote not found or this link has expired.');
          }
        }
      } catch (err) {
        const errBody = err.response?.data;
        const invTokErr =
          extractInvoiceShareToken(errBody?.data) || extractInvoiceShareToken(errBody);
        if (invTokErr && tryRedirectToInvoice(invTokErr)) return;

        const resolved = await fetchInvoiceShareTokenByQuoteShare(apiUrl, token);
        if (resolved && tryRedirectToInvoice(resolved)) return;

        if (!cancelled) setError('This quote link is invalid or has expired.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchQuote();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  const respond = async (action) => {
    if (responding) return;
    setResponding(true);
    try {
      const res = await axios.patch(`${apiUrl}/invoice/quotes/public/${token}/respond`, { action });
      if (res.data.success) {
        const payload = res.data.data || {};
        const merged = { ...quote, ...payload, status: action };
        setQuote(merged);
        setResponded(true);
        if (action === 'accepted') {
          const invTok = extractInvoiceShareToken(payload) || extractInvoiceShareToken(merged);
          if (invTok) {
            navigate(`/invoice/view/${invTok}`, { replace: true });
          }
        }
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to submit response. Please try again.';
      alert(msg);
    } finally {
      setResponding(false);
    }
  };

  const handlePrint = () => window.print();

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (error || !quote) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="max-w-md text-center p-8 bg-white rounded-lg shadow-md">
          <div className="text-5xl mb-4">&#128274;</div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Quote Not Available</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const customer = quote.Customer || {};
  const lines = quote.InvoiceLines || [];
  const total = parseFloat(quote.total_amount) || 0;
  const subtotal = lines.reduce((s, l) => s + parseFloat(l.quantity) * parseFloat(l.unit_price), 0);
  const discountValue = parseFloat(quote.discount_value) || 0;
  const discountAmt = quote.discount_type === 'percent' ? subtotal * discountValue / 100 : discountValue;
  const badge = STATUS_BADGE[quote.status] || { label: quote.status, cls: 'bg-gray-100 text-gray-600' };
  const canRespond = ['draft', 'sent'].includes(quote.status) && !responded;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 print:bg-white print:py-0 print:px-0">
      <div className="max-w-3xl mx-auto bg-white shadow-md rounded-lg overflow-hidden print:shadow-none">

        {/* ── Brand header ────────────────────────────────────────────── */}
        <div className="bg-blue-700 text-white px-8 py-6 flex items-center justify-between print:bg-white print:text-gray-900 print:border-b">
          <div className="flex items-center gap-4">
            {logoUrl && (
              <img src={logoUrl} alt="Company Logo" className="h-14 object-contain bg-white rounded p-1" />
            )}
            <div>
              {companyLegalName ? (
                <p className="text-sm font-semibold text-blue-100 print:text-gray-700 mb-0.5">{companyLegalName}</p>
              ) : null}
              <h1 className="text-2xl font-bold tracking-tight">Quote</h1>
              <p className="text-blue-200 text-sm print:text-gray-500">#{quote.quote_number}</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${badge.cls}`}>
            {badge.label}
          </span>
        </div>

        <div className="px-8 py-6 space-y-6">

          {/* ── Meta row ────────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Quote Date</p>
              <p className="font-medium text-gray-900">{quote.date || '—'}</p>
            </div>
            {quote.due_date && (
              <div>
                <p className="text-gray-500">Valid Until</p>
                <p className="font-medium text-gray-900">{quote.due_date}</p>
              </div>
            )}
          </div>

          {/* ── Customer info ────────────────────────────────────────────── */}
          <div className="border border-gray-100 rounded-lg p-4 bg-gray-50">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Prepared For</p>
            <p className="font-semibold text-gray-900 text-lg">
              {customer.firstname} {customer.lastname}
            </p>
            {customer.email && <p className="text-sm text-gray-600">{customer.email}</p>}
            {customer.phone && <p className="text-sm text-gray-600">{customer.phone}</p>}
          </div>

          {/* ── Line items ──────────────────────────────────────────────── */}
          <div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-2 text-gray-600 font-semibold">Description</th>
                  <th className="text-right py-2 text-gray-600 font-semibold w-16">Qty</th>
                  <th className="text-right py-2 text-gray-600 font-semibold w-28">Unit Price</th>
                  <th className="text-right py-2 text-gray-600 font-semibold w-28">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lines.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-6 text-gray-400 italic">No line items</td>
                  </tr>
                ) : (
                  lines.map((line, i) => (
                    <tr key={i} className="py-2">
                      <td className="py-3 text-gray-800">{line.description || '—'}</td>
                      <td className="py-3 text-right text-gray-700">{parseFloat(line.quantity)}</td>
                      <td className="py-3 text-right text-gray-700">${parseFloat(line.unit_price).toFixed(2)}</td>
                      <td className="py-3 text-right font-medium text-gray-900">
                        ${(parseFloat(line.quantity) * parseFloat(line.unit_price)).toFixed(2)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200">
                  <td colSpan={3} className="text-right py-2 text-gray-500 text-sm">Subtotal</td>
                  <td className="text-right py-2 text-gray-700 text-sm">${subtotal.toFixed(2)}</td>
                </tr>
                {discountAmt > 0 && (
                  <tr>
                    <td colSpan={3} className="text-right py-1 text-gray-500 text-sm">
                      Discount {quote.discount_type === 'percent' ? `(${discountValue}%)` : ''}
                    </td>
                    <td className="text-right py-1 text-red-600 text-sm">- ${discountAmt.toFixed(2)}</td>
                  </tr>
                )}
                <tr className="border-t-2 border-gray-200">
                  <td colSpan={3} className="text-right py-3 font-bold text-gray-800 text-base">Total</td>
                  <td className="text-right py-3 font-bold text-blue-700 text-xl">${total.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* ── Notes ───────────────────────────────────────────────────── */}
          {quote.notes && (
            <div className="border-l-4 border-blue-300 pl-4 py-2 bg-blue-50 rounded">
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">Notes</p>
              <p className="text-sm text-gray-700 whitespace-pre-line">{quote.notes}</p>
            </div>
          )}

          {/* ── Customer response ────────────────────────────────────────── */}
          {canRespond && (
            <div className="border border-gray-200 rounded-lg p-5 bg-white">
              <h3 className="font-semibold text-gray-800 mb-1">Ready to proceed?</h3>
              <p className="text-sm text-gray-500 mb-4">Please accept or decline this quote. You can contact us if you have any questions.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => respond('accepted')}
                  disabled={responding}
                  className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-semibold disabled:opacity-50"
                >
                  {responding ? 'Submitting...' : '✓ Accept Quote'}
                </button>
                <button
                  onClick={() => respond('declined')}
                  disabled={responding}
                  className="px-6 py-2 border border-red-400 text-red-600 rounded-md hover:bg-red-50 font-semibold disabled:opacity-50"
                >
                  Decline
                </button>
              </div>
            </div>
          )}

          {/* ── Confirmation banner ──────────────────────────────────────── */}
          {responded && (
            <div className={`rounded-lg p-4 text-center font-semibold ${
              quote.status === 'accepted' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-gray-50 text-gray-700 border border-gray-200'
            }`}>
              {quote.status === 'accepted'
                ? '✓ Thank you! Your acceptance has been recorded. We will be in touch shortly.'
                : 'You have declined this quote. Please contact us if you would like to discuss further.'}
            </div>
          )}

          {/* Already responded (not in current session) */}
          {!canRespond && !responded && quote.status !== 'draft' && quote.status !== 'sent' && (
            <div className={`rounded-lg p-4 text-center text-sm border ${badge.cls}`}>
              This quote has been <strong>{quote.status}</strong>.
            </div>
          )}
        </div>

        {/* ── Footer / print action ────────────────────────────────────── */}
        <div className="px-8 py-4 bg-gray-50 border-t flex justify-end gap-2 print:hidden">
          <button
            onClick={handlePrint}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-white"
          >
            &#128438; Print / Save PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuotePublicView;
